import { diaryDayFacts } from './diary-layout'
import { isContact, isUnplaced } from './planning'
import type { Clean, RoundDay } from './types'
import { addDaysISO, dayLabel, mondayOf, money, shortDate } from './ui'

// Book a visit's "Which day?" (Liam approved the design, 9 Oct 2026: canvas VKDK3zCXygGF8dgMxq593n, artboard New).
// The pure half, so the checks read the rules the form draws: which days are offered, how full each one is (by the
// Diary's own day rule), who is out that day, and what the lemon button says.

// Six days from today, as drawn. A day that has gone is never offered for a new booking or a move; the form's own
// dates (the day Roundly suggests, the day the diary was on, the day they accepted, the day a visit is saved on) join
// them, in order, when they fall outside them. `saved` is the day an existing visit is saved on: kept even when it has
// gone, because a visit can be changed without moving it (docs/DIARY-AND-MESSAGES.md section 9).
export const DAY_CHIPS = 6

export function chipDates(today: string, own: string | null | undefined | (string | null | undefined)[], count = DAY_CHIPS, saved: string | null = null): string[] {
  const dates = Array.from({ length: count }, (_, i) => addDaysISO(today, i))
  for (const d of Array.isArray(own) ? own : [own]) if (d && (d >= today || d === saved) && !dates.includes(d)) dates.push(d)
  return dates.sort()
}

export interface DayLoad {
  visits: number // placed visits, booked or done (the Diary's day card)
  pence: number
  unpriced: boolean
  suggested: number // Roundly's own suggestions, not booked
  worker: string | null // who the diary has out that day (round_days)
}

// The Diary's day rule (schedule.ts dayRows, diary-layout.ts diaryDayFacts): visits on that date that are not
// contact reminders, not held in a week or brought in from a sheet with no day, and not cancelled; suggestions counted
// apart. `exclude` leaves out one visit (the form counts the visit being edited on its own day, as the Diary does).
export function dayLoad(rows: readonly Clean[], date: string, roster: readonly RoundDay[], exclude?: number): DayLoad {
  const day = rows.filter((c) => c.date === date && c.id !== exclude && !isContact(c) && !isUnplaced(c, date, date))
  const f = diaryDayFacts(day)
  const worker = roster.find((r) => r.date === date)?.worker?.trim() || null
  return { visits: f.visits, pence: f.pence, unpriced: f.unpriced, suggested: f.suggested, worker }
}

// "no visits", "1 visit", "4 visits": never "free" (nothing booked is not proof anyone is available).
export function visitsWords(n: number): string {
  return n === 0 ? 'no visits' : `${n} visit${n === 1 ? '' : 's'}`
}

// The line under the days: "Fri 9 Oct: 4 visits booked, £505 · Dave out that day"; on the day a visit being edited is
// saved on, "1 visit booked (this one)" or "3 visits booked, this one included".
export function dayLineWords(date: string, load: DayLoad, showMoney = true, thisOne = false): string {
  const which = thisOne ? (load.visits === 1 ? ' (this one)' : ', this one included') : ''
  const booked = load.visits
    ? `${visitsWords(load.visits)} booked${which}${showMoney && load.pence ? `, ${money(load.pence)}` : ''}${showMoney && load.unpriced ? `${load.pence ? ' + ' : ', '}not all priced` : ''}`
    : 'no visits booked yet'
  const suggested = load.suggested ? `, ${load.suggested} suggested` : ''
  return `${dayLabel(date)}: ${booked}${suggested}${load.worker ? ` · ${load.worker} out that day` : ''}`
}

// The lemon button names what it books: "Book Fri 9 Oct", "Book Fri 9 Oct, 10:00", "Book week of 5 Oct"; a visit moved
// to another day, "Move to Mon 12 Oct".
export function bookWords(date: string, time: string, weekOnly: boolean, verb = 'Book'): string {
  if (!date) return verb
  if (weekOnly) return `${verb} week of ${shortDate(mondayOf(date))}`
  return `${verb} ${dayLabel(date)}${time.trim() ? `, ${time.trim()}` : ''}`
}

// How long: the presets drawn, plus whatever is saved when it is not one of them.
export const DURATION_PRESETS: [number, string][] = [[30, '30 min'], [45, '45 min'], [60, '1 hour']]
export function minutesWords(m: number): string {
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60), r = m % 60
  return `${h} hour${h === 1 ? '' : 's'}${r ? ` ${r} min` : ''}`
}

// A one-person round (Liam, 19 Oct 2026): the name on a visit worth saying, which is any name but the owner's own (so a
// visit left in someone else's name can be seen and cleared). Blank for no name or the owner's, whatever the capitals.
export function otherThanOwner(name: string | null | undefined, owner: string | null | undefined): string {
  const n = (name ?? '').trim()
  return n && n.toLowerCase() !== (owner ?? '').trim().toLowerCase() ? n : ''
}

// Who's doing it: the day's worker (a blank name means whoever the diary has out that day, functions/lib/worker-day.ts),
// then the round's other workers, then whatever name is already on the visit.
export function workerChoices(dayWorker: string | null, known: readonly string[], current: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  const add = (name: string): void => {
    const n = name.trim()
    if (!n || seen.has(n.toLowerCase())) return
    seen.add(n.toLowerCase()); out.push(n)
  }
  if (dayWorker) seen.add(dayWorker.trim().toLowerCase())
  for (const w of known) add(w)
  if (current.trim() && current.trim().toLowerCase() !== dayWorker?.trim().toLowerCase()) add(current)
  return out
}

// " · Now Liam's" when a move gave the visit the name of whoever the new day is set to (functions/api/cleans/[id].ts).
export function nowWhose(cl: { worker?: string | null }, row: unknown): string {
  const name = (row as { worker?: string | null } | undefined)?.worker?.trim()
  return name && name !== (cl.worker ?? '').trim() ? ` · Now ${name}'s` : ''
}
